/**
 * useQuery hook for the saved PDF styles of a publication or section.
 *
 * The flow:
 *
 *   1. The view calls this hook on mount with `{ exportType, exportId }`.
 *   2. `useGraphQLQuery` fetches the row via Hasura and applies the same
 *      normalization the effect did (parse tocStyles JSON, shape
 *      publication/section IDs based on `exportType`).
 *   3. Consumers read `data` / `isLoading` off the hook result directly.
 *
 * Pass `null` to disable the query (e.g. during a route transition before
 * an export ID is known). The caller — not the hook — picks the mode.
 *
 * Post-write invalidation:
 *   `useUpsertPdfStyles` and `useDeletePdfStyles` write into the cache via
 *   `queryClient.setQueryData` with the narrow `publicationSetupPdfQueryKeys.styles(params)`
 *   key, keeping the write path authoritative over refresh-on-save.
 *
 * Note: `exportPdfSettings` is not in the @opengov/bnp-stories-hasura-service-schema
 * package so we use a raw string document rather than a codegen graphql() tagged template.
 * The `useGraphQLQuery` hook accepts `document: TypedDocumentNode | string`.
 */

import { useGraphQLQuery } from '@opengov/bnp-core';
import { IPublicationSetupPdfStyle } from '@opengov/stories-shared/constants';

import {
  publicationSetupPdfQueryKeys,
  PublicationSetupPdfStylesKeyParams
} from './publicationSetupPdfQueryKeys';
import { HASURA_SERVICE_URL } from '../../../../../config/config';

const GET_EXPORT_PDF_SETTINGS_QUERY = `
  query GetExportPdfSettingsForSetupPdf($exportId: uuid!, $exportType: String!) {
    publicationSetupPdfStyles: exportPdfSettings(where: { exportType: { _eq: $exportType }, exportId: { _eq: $exportId } }) {
      id
      exportType
      exportId
      coverStoryId
      createdBy
      pageMargins
      headerControls
      footerControls
      tocStyles
      tocData
      watermark
      storyStates
      createdAt
      updatedAt
    }
  }
`;

/** Raw server shape — `tocStyles` is JSON-encoded on the wire. */
type IPublicationSetupPdfStyleServerShape = Omit<
  IPublicationSetupPdfStyle,
  'tocStyles' | 'publicationId' | 'sectionId'
> & {
  tocStyles: string;
  exportType: 'publication' | 'section';
  exportId: string;
};

type IGetExportPdfSettingsData = {
  publicationSetupPdfStyles: IPublicationSetupPdfStyleServerShape[];
};

export type UsePublicationSetupPdfStylesParams = PublicationSetupPdfStylesKeyParams | null;

export function usePublicationSetupPdfStyles(
  params: UsePublicationSetupPdfStylesParams
) {
  return useGraphQLQuery<IGetExportPdfSettingsData, { exportId: string; exportType: string }, IPublicationSetupPdfStyle | null>({
    queryKey: params
      ? publicationSetupPdfQueryKeys.styles(params)
      : ([...publicationSetupPdfQueryKeys.all, 'styles', 'disabled'] as const),
    document: GET_EXPORT_PDF_SETTINGS_QUERY,
    url: HASURA_SERVICE_URL,
    variables: params
      ? { exportId: params.exportId, exportType: params.exportType }
      : { exportId: '', exportType: '' },
    options: {
      enabled: params !== null,
      select: (data: IGetExportPdfSettingsData | null): IPublicationSetupPdfStyle | null => {
        // useDeletePdfStyles writes `null` to the cache after a successful delete,
        // so select() can be invoked with data=null on the discard flow.
        if (!data) return null;
        const rows = data.publicationSetupPdfStyles;
        if (!rows || rows.length === 0) return null;

        // Apply the same normalization the effect did: parse tocStyles JSON and
        // map `exportType` back into explicit `publicationId` / `sectionId` fields
        // so consumers of `IPublicationSetupPdfStyle` see the shape they expect.
        const { tocStyles, exportType: resExportType, exportId: resExportId, ...rest } = rows[0];
        return {
          ...rest,
          tocStyles: JSON.parse(tocStyles),
          publicationId: resExportType === 'publication' ? resExportId : null,
          sectionId: resExportType === 'section' ? resExportId : null
        } as IPublicationSetupPdfStyle;
      }
    }
  });
}
